import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { uploadFileToS3 } from "Pages/GcPrecon/GcPreconCommercial/UploadFileToS3";
import {
  downloadPreconPdf,
  generatePreconPdfBlob,
  preconPdfFileName,
} from "hooks/usePreconPdf";
import { FormikProvider, useFormik } from "formik";
import StepsBar from "../components/StepsBar";
import PublishBar from "../components/PublishBar";
import SuccessScreen from "../components/SuccessScreen";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import usePreconReport from "../hooks/usePreconReport";
import { STEP_SCHEMAS } from "../utils/validation";
import { buildPreconPayload } from "../utils/payload";
import {
  savePreconDraft,
  publishPrecon,
} from "../../../../services/projectService";
import { PreconFormValues } from "../types/precon.types";
import PreconAuthoringSkeleton from "../../../../Components/Common/Skeleton/PreconAuthoringSkeleton";
import Step1ProjectSetup from "../components/steps/Step1ProjectSetup";
import Step2Certification from "../components/steps/Step2Certification";
import Step3ExecutiveSummary from "../components/steps/Step3ExecutiveSummary";
import Step4ContractDelivery from "../components/steps/Step4ContractDelivery";
import Step5Allowances from "../components/steps/Step5Allowances";
import Step6BudgetAnalysis from "../components/steps/Step6BudgetAnalysis";
import Step7ScheduleActions from "../components/steps/Step7ScheduleActions";
import Step8PreviewPublish from "../components/steps/Step8PreviewPublish";
import NetworkError from "Components/Common/NetworkError/NetworkError";

const STEP_LABELS = [
  "Project Setup",
  "Certification",
  "Executive Summary",
  "Contract Delivery",
  "Allowances & Contingencies",
  "Budget Analysis",
  "Schedule, Closing & Actions",
  "Preview & Publish",
];

const NEXT_LABELS = [
  "Next: Certification",
  "Next: Executive Summary",
  "Next: Contract Delivery",
  "Next: Allowances & Contingencies",
  "Next: Budget Analysis",
  "Next: Schedule, Closing & Actions",
  "Next: Preview & Publish",
];

const steps = STEP_LABELS.map((label, i) => ({ id: `step-${i + 1}`, label }));

type ApiError = { message?: string; raw?: { fields?: Record<string, string> } };
type ApiResponse = { error: ApiError | null };

const getApiErrorMessage = (error: ApiError, fallback: string): string => {
  const fields = error.raw?.fields;
  if (fields) {
    const msgs = Object.values(fields);
    if (msgs.length > 0) return msgs[0];
  }
  return error.message ?? fallback;
};

const parseYupErrors = (err: unknown): Record<string, string> => {
  if (!err || typeof err !== "object" || !("inner" in err)) return {};
  const yupErr = err as {
    path?: string;
    message: string;
    inner: { path: string; message: string }[];
  };
  const list =
    yupErr.inner.length > 0
      ? yupErr.inner
      : yupErr.path
        ? [{ path: yupErr.path, message: yupErr.message }]
        : [];
  const map: Record<string, string> = {};
  list.forEach((e) => {
    if (e.path) map[e.path] = e.message;
  });
  return map;
};

const scrollToFirstError = (errors: Record<string, string>): boolean => {
  for (const key of Object.keys(errors)) {
    const el =
      document.querySelector<HTMLElement>(`[name="${key}"]`) ??
      document.getElementById(key);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus?.();
      return true;
    }
  }
  return false;
};

const PreconAuthoringPage = () => {
  const { projectId = "" } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const showToast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(() => {
    const s = Number(searchParams.get("step"));
    return s >= 1 && s <= steps.length ? s : 1;
  });

  const goToStep = (step: number) => {
    setCurrentStep(step);
    setSearchParams({ step: String(step) }, { replace: true });
  };
  const [published, setPublished] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const ccErrorRef = useRef<{ hasError: boolean; scrollFn: () => void }>({
    hasError: false,
    scrollFn: () => {},
  });

  const [savedDraft] = useState<Partial<PreconFormValues> | null>(() => {
    try {
      const raw = localStorage.getItem(`precon_draft_${projectId}`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const {
    initialValues,
    prefilledFields,
    loading,
    error: fetchError,
  } = usePreconReport(projectId);

  const mergedInitialValues = useMemo(
    () => (savedDraft ? { ...initialValues, ...savedDraft } : initialValues),
    [initialValues, savedDraft],
  );

  useScrollToTop(currentStep, { skipFirstRender: false });

  const currentStepRef = useRef(currentStep);
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const formik = useFormik<PreconFormValues>({
    initialValues: mergedInitialValues,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validate: async (values) => {
      try {
        await STEP_SCHEMAS[currentStepRef.current - 1].validate(values, {
          abortEarly: false,
        });
        return {};
      } catch (err: unknown) {
        return parseYupErrors(err);
      }
    },
    onSubmit: async (values) => {
      try {
        const fileName = preconPdfFileName(values.projectName);
        const blob = await generatePreconPdfBlob(values);
        const uploadResult: any = await uploadFileToS3(
          new File([blob], fileName, { type: "application/pdf" }),
        );
        const pdfKey = uploadResult?.key;
        if (!pdfKey) throw new Error("PDF upload failed — no key returned");

        const payload = {
          ...buildPreconPayload(values),
          preconAuthoringPdf: { key: pdfKey, fileName },
        };
        const result = (await publishPrecon(projectId, payload)) as ApiResponse;
        if (result.error) {
          showToast(
            getApiErrorMessage(result.error, "Failed to publish report."),
          );
          return;
        }
        localStorage.removeItem(`precon_draft_${projectId}`);
        setPublished(true);
        showToast("Report published successfully");
      } catch (err) {
        showToast((err as Error).message || "Failed to publish report.");
      }
    },
  });

  useEffect(() => {
    const manualValues = Object.fromEntries(
      Object.entries(formik.values).filter(
        ([key]) => !prefilledFields.has(key as keyof PreconFormValues),
      ),
    );
    localStorage.setItem(
      `precon_draft_${projectId}`,
      JSON.stringify(manualValues),
    );
  }, [formik.values, prefilledFields]);

  const handleNext = async () => {
    const hasVisibleCharError = Array.from(
      document.querySelectorAll<HTMLElement>(".field-error"),
    ).some((el) => el.offsetParent !== null);
    if (hasVisibleCharError) {
      const el = Array.from(
        document.querySelectorAll<HTMLElement>(".field-error"),
      ).find((el) => el.offsetParent !== null);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const schema = STEP_SCHEMAS[currentStep - 1];
    try {
      await schema.validate(formik.values, { abortEarly: false });
      goToStep(Math.min(currentStep + 1, steps.length));
    } catch (err: unknown) {
      const errorMap = parseYupErrors(err);
      const touchedMap = Object.fromEntries(
        Object.keys(errorMap).map((k) => [k, true]),
      );
      formik.setErrors(errorMap);
      formik.setTouched(touchedMap, false);
      // Try named/id fields first; fall back to visible .field-error after re-render
      const scrolled = scrollToFirstError(errorMap);
      if (!scrolled) {
        setTimeout(() => {
          const el = Array.from(
            document.querySelectorAll<HTMLElement>(
              ".field-error, .error-input-red",
            ),
          ).find((el) => el.offsetParent !== null);
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 50);
      }
    }
  };

  const handleBack = () => {
    goToStep(Math.max(currentStep - 1, 1));
  };

  const handleSaveDraft = async () => {
    if (ccErrorRef.current.hasError) {
      ccErrorRef.current.scrollFn();
      return;
    }
    try {
      await STEP_SCHEMAS[currentStep - 1].validate(formik.values, {
        abortEarly: false,
      });
    } catch (err: unknown) {
      const errorMap = parseYupErrors(err);
      const touchedMap = Object.fromEntries(
        Object.keys(errorMap).map((k) => [k, true]),
      );
      formik.setErrors(errorMap);
      formik.setTouched(touchedMap, false);
      scrollToFirstError(errorMap);
      return;
    }
    setSavingDraft(true);
    const payload = buildPreconPayload(formik.values);
    const result = (await savePreconDraft(projectId, payload)) as ApiResponse;
    setSavingDraft(false);
    if (result.error) {
      showToast(getApiErrorMessage(result.error, "Failed to save draft."));
      return;
    }
    showToast("Draft saved");
    localStorage.removeItem(`precon_draft_${projectId}`);
  };

  const handlePublish = async () => {
    if (ccErrorRef.current.hasError) {
      ccErrorRef.current.scrollFn();
      return;
    }
    for (let i = 0; i < STEP_SCHEMAS.length; i++) {
      try {
        await STEP_SCHEMAS[i].validate(formik.values, { abortEarly: false });
      } catch (err: unknown) {
        if (err && typeof err === "object" && "inner" in err) {
          const yupErr = err as { inner: { path: string; message: string }[] };
          const errorMap: Record<string, string> = {};
          const touchedMap: Record<string, boolean> = {};
          yupErr.inner.forEach((e) => {
            if (e.path) {
              errorMap[e.path] = e.message;
              touchedMap[e.path] = true;
            }
          });
          formik.setErrors(errorMap);
          formik.setTouched(touchedMap, false);
          goToStep(i + 1);
          setTimeout(() => scrollToFirstError(errorMap), 100);
        }
        return;
      }
    }
    formik.submitForm();
  };

  const handleRestart = () => {
    localStorage.removeItem(`precon_draft_${projectId}`);
    setPublished(false);
    goToStep(1);
    formik.resetForm();
    navigate("/admin");
  };

  const handleDownloadPdf = async () => {
    try {
      await downloadPreconPdf(formik.values);
    } catch (err) {
      showToast((err as Error).message || "Failed to download report PDF.");
    }
  };

  const renderStep = () => {
    const props = { prefilledFields };
    switch (currentStep) {
      case 1:
        return <Step1ProjectSetup {...props} />;
      case 2:
        return <Step2Certification {...props} />;
      case 3:
        return <Step3ExecutiveSummary {...props} />;
      case 4:
        return <Step4ContractDelivery {...props} />;
      case 5:
        return <Step5Allowances {...props} />;
      case 6:
        return <Step6BudgetAnalysis {...props} />;
      case 7:
        return <Step7ScheduleActions {...props} />;
      case 8:
        return (
          <Step8PreviewPublish
            onToast={showToast}
            onSaveDraft={handleSaveDraft}
            onCcErrorChange={(hasError, scrollFn) => {
              ccErrorRef.current = { hasError, scrollFn };
            }}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <PreconAuthoringSkeleton />;
  }

  if (fetchError) {
    return <NetworkError />;
  }

  const isLocked = formik.isSubmitting || savingDraft;

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <fieldset className="pageLocker" disabled={isLocked}>
          <StepsBar
            steps={steps}
            currentStep={published ? steps.length : currentStep}
            onStepChange={() => {}}
          />
          <div className="page">
            {published ? (
              <SuccessScreen
                onViewProject={() => navigate(`/project-detail/${projectId}`)}
                onDownload={handleDownloadPdf}
                onRestart={handleRestart}
                projectName={formik.values.projectName}
                bankName={formik.values.bank}
                ownerName={formik.values.ownerName}
                gcName={formik.values.gcName}
                deliverTo={formik.values.deliverTo}
                deliveryCC={formik.values.deliveryCC}
                invoiceNumber={formik.values.invoiceNumber}
                invoiceAmount={formik.values.invoiceAmount}
              />
            ) : (
              renderStep()
            )}
          </div>
          {!published && (
            <PublishBar
              currentStep={currentStep}
              totalSteps={steps.length}
              label={STEP_LABELS[currentStep - 1]}
              nextLabel={NEXT_LABELS[currentStep - 1]}
              onBack={handleBack}
              onNext={handleNext}
              onSaveDraft={handleSaveDraft}
              onPublish={handlePublish}
              isLast={currentStep === steps.length}
              loading={formik.isSubmitting}
              savingDraft={savingDraft}
            />
          )}
        </fieldset>
      </form>
    </FormikProvider>
  );
};

export default PreconAuthoringPage;
